import * as React from "react";
import { useTranslation, withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { ColumnProps } from "antd/lib/table";
import { Button } from "Components/General/Button";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { workflowsStore } from "Pages/Admin/Stores/WorkflowsStore";
import { dayjs } from "Config/dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { WorkflowForm } from "./WorkflowForm";
import { Tag, Row, Tooltip } from "antd";
import classnames from "classnames";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";
import { WorkflowTableRowActions } from "../Components/WorkflowTableRowActions";
import { Table } from "Components/General/Table";

interface WorkflowsProps extends WithTranslation, WithRouterProps {}

const MODAL_PROPS = { width: `100%`, style: { maxWidth: 1100 }, footer: null, destroyOnHidden: true };

const CheckIcon = ({ value }: { value: boolean }) => {
  return (
    <FontAwesomeIcon
      className={classnames({ "dusty-gray": !value })}
      icon={["far", `${value ? "check" : "times"}`] as IconProp}
    />
  );
};

const AfterUploadIcon = ({
  checkIconValue,
  iconWithTooltip,
}: {
  checkIconValue: boolean;
  iconWithTooltip: boolean;
}) => {
  const { t } = useTranslation(I18N_NS.ADMIN);
  if (iconWithTooltip) {
    return (
      <Tooltip title={t("Workflow will be canceled, if a new version of a included file will be uploaded")}>
        <span>
          <FontAwesomeIcon fixedWidth icon={["far", "shield-check"]} />
        </span>
      </Tooltip>
    );
  }
  return <CheckIcon value={checkIconValue} />;
};

export const Workflows = withTranslation(I18N_NS.ADMIN)(
  withRouter(
    observer(
      class extends React.Component<WorkflowsProps> {
        componentDidMount() {
          (async () => {
            await workflowsStore.fetch(this.props.params.id);
          })();
        }

        componentWillUnmount() {
          workflowsStore.clear();
        }

        handleEdit = async (obj: API_AdminWorkflowDefinitionParameters) => {
          await workflowsStore.edit(obj);
        };

        createWorkflow = async (obj: API_AdminWorkflowDefinitionParameters) => {
          await workflowsStore.save(obj);
        };

        render() {
          const { t } = this.props;

          const columns: ColumnProps<API_AdminWorkflowDefinitionDetails>[] = [
            {
              dataIndex: "name",
              title: t("Name"),
            },
            {
              dataIndex: "description",
              title: t("Description"),
            },
            {
              dataIndex: "created",
              title: t("Created"),
              width: 120,
              render: (text) => (text ? dayjs(text).format("lll") : "-"),
            },
            {
              dataIndex: "startEventManually",
              title: <Tooltip overlay={t("Start event - Manually")}>Start event - M</Tooltip>,
              width: 120,
              render: (text) => <CheckIcon value={text} />,
            },
            {
              dataIndex: "startEventAfterUpload",
              title: <Tooltip overlay={t("Start event - After upload")}>Start event - AUp</Tooltip>,
              width: 120,
              render: (text, record) => (
                <AfterUploadIcon iconWithTooltip={record.cancelIfNewFileversionIsUploaded} checkIconValue={text} />
              ),
            },
            {
              dataIndex: "startEventAfterUploadWorkflow",
              title: <Tooltip overlay={t("Start event - After upload workflow")}>Start event - AUpW</Tooltip>,
              width: 130,
              render: (text) => <CheckIcon value={text} />,
            },
            {
              dataIndex: "startEventFileFunction",
              title: <Tooltip overlay={t("Start event - File function")}>Start event - DF</Tooltip>,
              width: 120,
              render: (text) => <CheckIcon value={text} />,
            },
            {
              dataIndex: "startEventMultiFileFunction",
              title: <Tooltip overlay={t("Start event - Multi file function")}>Start event - MDF</Tooltip>,
              width: 130,
              render: (text) => <CheckIcon value={text} />,
            },
            {
              dataIndex: "startEventMultiMailFunction",
              title: <Tooltip overlay={t("Start event - Multi mail function")}>Start event - MMF</Tooltip>,
              width: 130,
              render: (text) => <CheckIcon value={text} />,
            },
            {
              dataIndex: "startEventDocMenu",
              title: <Tooltip overlay={t("Start event - DocMenu")}>Start event - DM</Tooltip>,
              width: 130,
              render: (text) => <CheckIcon value={text} />,
            },
            {
              dataIndex: "active",
              title: t("State"),
              width: 100,
              align: "center",
              render: (text) => <Tag color={text ? "green" : "red"}>{text ? t("Active") : t("Inactive")}</Tag>,
            },
            {
              dataIndex: "action",
              width: 40,
              align: "center",
              render: (text, record) => (
                <WorkflowTableRowActions data={record}>
                  <div className="docs-dropdown-trigger" onClick={(e) => e.stopPropagation()}>
                    <FontAwesomeIcon className="email-action-icon" icon={["far", "ellipsis-v"]} fixedWidth={true} />
                  </div>
                </WorkflowTableRowActions>
              ),
            },
          ];

          return (
            <div className="g-card extended-layout">
              <Row align="middle" justify="space-between" className="mb-lg">
                <h1>{t("Manage workflow")}</h1>
                <ModalConsumer>
                  {({ showModal }) => (
                    <div className="text-right">
                      <Button
                        type="primary"
                        faIcon={"plus"}
                        onClick={() =>
                          showModal(WorkflowForm, {
                            modalProps: { ...MODAL_PROPS, title: t("New workflow") },
                            componentProps: {
                              onCreate: this.createWorkflow,
                            },
                          })
                        }
                      >
                        {t("New workflow")}
                      </Button>
                    </div>
                  )}
                </ModalConsumer>
              </Row>
              <Table<API_AdminWorkflowDefinitionDetails>
                name="adminWorkflows"
                columns={columns}
                dataSource={workflowsStore.data}
                loading={workflowsStore.state === "LOADING"}
                rowKey="id"
                pagination={false}
                // scroll={{ x: 1915 }}
              />
            </div>
          );
        }
      }
    )
  )
);
